import React from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useTRPC } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { toast } from "sonner";
import * as Icons from "lucide-react"

interface Props {
    value?: string;
    onValueChange?: (value:string | undefined) => void;
}

const SelectUser = ({value, onValueChange}:Props) => {
    const { trpc } = useTRPC();
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [selectedValue, setselectedValue] = React.useState<string | undefined>(value);

    const { data:users } = useQuery({
        queryKey: ["users"],
        queryFn: async () => {
            const users = await trpc.users.search.query({})
            return users;
        },
        onError: (err) => {
            if(err instanceof Error) {
                toast.error(err.message);
            } else if(err instanceof TRPCClientError) {
                toast.error(err.message);
            } else {
                toast.error("Er is een onbekende fout opgetreden.");
            }
        }
    })

    const handleSelectUser = (user_id:string) => {
        if(selectedValue === user_id) {
            setselectedValue(undefined);
            onValueChange?.(undefined);
        } else {
            setselectedValue(user_id);
            onValueChange?.(user_id);
        }
        
        setOpen(false);
    }

    const filterUsers = (data: NonNullable<typeof users>['users']) => {
        if(!search) return data;
        return data.filter((user) => 
            user?.first_name?.toLowerCase().includes(search.toLowerCase()) || 
            user?.last_name?.toLowerCase().includes(search.toLowerCase()) ||
            user?.email?.toLowerCase().includes(search.toLowerCase())
        );
    }

    const selectedUser = users?.users?.find(u => u.id === selectedValue);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
            <Button variant='outline' className="w-full" onClick={(e)=>{e.stopPropagation()}}>
                {selectedUser
                ? `${selectedUser.first_name} ${selectedUser.last_name}`
                : 'Selecteer gebruiker...'
                }
            </Button>            
        </DialogTrigger>
        <DialogContent className="p-0 rounded-3xl gap-0 overflow-hidden" hideCloseButton>
            <div className="flex items-center relative">
                <input 
                    value={search} 
                    onChange={(e)=>{setSearch(e.currentTarget.value)}} 
                    placeholder="Zoek naar gebruiker" 
                    className="focus:outline-none py-3 px-4 bg-input/50 rounded-3xl text-sm w-full h-12"
                />
                <button onClick={()=>{setOpen(false)}} className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground size-12 aspect-square hover:bg-muted rounded-full flex items-center justify-center">
                    <Icons.X className="w-4 h-4 text-muted-foreground"/>
                </button>
            </div>
            
            {(users && users?.users?.length > 0) && 
            <div className="max-h-96 overflow-auto flex flex-col gap-1 p-2">
                {filterUsers(users?.users)?.map((user)=>(
                    <button onClick={()=>{handleSelectUser(user.id)}} className="flex items-center justify-between gap-4 p-2 rounded-xl hover:bg-muted text-left" key={user.id}>
                        <div className="flex items-center gap-2">
                            <div className="size-10 aspect-square rounded-full bg-secondary flex items-center justify-center">
                                {user?.first_name?.charAt(0)}
                            </div>
                            <div>
                                <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                                <p className="text-xs text-muted-foreground">{user?.email}</p>
                            </div>
                        </div>
                        
                        {user.id === selectedValue && <Icons.Check className="w-4 h-4 text-foreground"/>}
                    </button>
                ))}
            </div>
            }
        </DialogContent>
    </Dialog>
  )
}

export default SelectUser